import { twMerge } from 'tailwind-merge';

export interface SegmentedControlOption {
  label: string;
  value: number | string;
}

interface SegmentedControlProps {
  options: SegmentedControlOption[];
  selectedValue: number | string;
  onChange: (value: number | string) => void;
  onOptionClick?: (value: number | string) => void;
  renderLabel: (
    option: SegmentedControlOption,
    isSelected: boolean,
  ) => React.ReactNode;
  getOptionClassName?: (
    option: SegmentedControlOption,
    isSelected: boolean,
  ) => string;
  mobileSheet?: boolean;
  disabled?: boolean;
  className?: string;
}

function SegmentedControl({
  options,
  selectedValue,
  onChange,
  onOptionClick,
  renderLabel,
  getOptionClassName,
  mobileSheet = false,
  disabled = false,
  className,
}: SegmentedControlProps) {
  const containerClassName = twMerge(
    'border-taupe-600 divide-taupe-600 grid max-w-max gap-y-2 divide-x overflow-hidden border shadow-[0px_1px_2px_0px_#1018280D] md:flex',
    mobileSheet
      ? 'max-w-none divide-none border-none shadow-none'
      : 'rounded-full',
    className,
  );

  return (
    <div className={containerClassName} role="radiogroup">
      {options.map((option) => {
        const isSelected = selectedValue === option.value;

        const optionClassName = twMerge(
          'relative flex cursor-pointer bg-white transition-all duration-75 hover:bg-taupe-400',
          mobileSheet &&
            'flex w-full items-center gap-x-2 border-solid border-black-300 rounded-xl border px-4 !py-0',
          isSelected &&
            mobileSheet &&
            'border-blue-300 bg-blue-50 border-solid',
          isSelected && !mobileSheet && 'bg-taupe-500 text-grey-800',
          disabled && 'opacity-50 cursor-not-allowed',
          getOptionClassName?.(option, isSelected),
        );

        const inputId = `segmented-control-${option.value}`;

        return (
          <button
            key={option.value}
            className={optionClassName}
            onClick={() => !disabled && onOptionClick?.(option.value)}
          >
            <input
              type="radio"
              name="segmented-control"
              id={inputId}
              value={option.value}
              checked={isSelected}
              onChange={() => !disabled && onChange(option.value)}
              disabled={disabled}
              className="sr-only"
            />

            <label
              htmlFor={inputId}
              className={twMerge(
                'font-figtree text-grey-800 cursor-pointer px-4 py-2.5 text-sm font-medium select-none',
                isSelected && mobileSheet && 'text-primary-600',
                mobileSheet && 'block w-full py-4!',
                disabled && 'cursor-not-allowed',
              )}
            >
              {renderLabel(option, isSelected)}
            </label>
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
